import React from 'react';
import { useTranslation } from 'react-i18next';

import { DialogTitle } from '@radix-ui/react-dialog';

import { Button, Icon, Input, Spinner } from '@/components/atoms';
import { CATEGORY_POPUP_TEST_IDS } from '@/constants/data-testids';
import { TranslationCategory } from '@/types/management/managementCategories';

type EditingData = {
  name: string;
  description: string;
};

type EditTranslationPanelProps = {
  editingOptionValue: TranslationCategory | null;
  editingData: EditingData;
  isLoadingSaveChanges?: boolean;
  onEditChange: (newValue: string, field: 'name' | 'description') => void;
  onCancel: () => void;
  onSave: () => void;
};

export const TranslationEditItem: React.FC<EditTranslationPanelProps> = ({
  editingOptionValue,
  editingData,
  isLoadingSaveChanges,
  onEditChange,
  onCancel,
  onSave,
}) => {
  const { t } = useTranslation('components');

  if (!editingOptionValue) return null;

  return (
    <div data-testid={CATEGORY_POPUP_TEST_IDS.STEP2_EDIT}>
      <DialogTitle className="text-body-20-semibold text-custom-warm-grey-800 mb-2">
        {t('categoryPopup.translationEditItem.title')}
      </DialogTitle>
      <div className="flex items-center gap-2 mb-5">
        <Icon name={editingOptionValue.languageCode} width={16} height={16} />
        <p className="grow">{editingOptionValue.name}</p>
      </div>
      <div className="mb-6 flex flex-col gap-5">
        <div>
          <Input
            placeholder="Enter category name"
            value={editingData.name}
            label="Category name"
            className="w-full"
            onChange={e => onEditChange(e.target.value, 'name')}
          />
        </div>
        <div>
          <Input
            placeholder="Enter description"
            as="textarea"
            label="Description"
            value={editingData.description}
            className="w-full"
            onChange={e => onEditChange(e.target.value, 'description')}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Button size="lg" variant="secondary" className="w-full" onClick={onCancel}>
          {t('categoryPopup.translationEditItem.cancel')}
        </Button>
        <Button size="lg" variant="primary" className="relative w-full" onClick={onSave}>
          {t('categoryPopup.translationEditItem.save')}{' '}
          {isLoadingSaveChanges && (
            <Spinner className="absolute top-1/2 -translate-y-1/2 right-8" />
          )}
        </Button>
      </div>
    </div>
  );
};
